/**
 * Locked recommendation engine.
 *
 * Selection order:
 *   1. same cluster as the current post
 *   2. configured same-audience neighbour clusters
 *   3. configured cross-audience neighbour clusters
 *   4. site-wide eligible fallback — only when fewer than 3 results so far
 *
 * Exclusions applied at every step:
 *   - current article id
 *   - exclude_from_recommendations = true
 *   - rows absent from blog_posts_renderable (caller drops these upstream)
 *   - duplicate id
 *   - duplicate slug
 *
 * Invariant: result.length === Math.min(3, eligible.length)
 *
 * There is NO arbitrary "latest 200" cap; callers may pass the full
 * renderable set or a reasonable per-query LIMIT.
 */
import { NEIGHBOURS, isTopicCluster, type TopicCluster } from "./featureRoutes";

export interface RecommendationCandidate {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  target_audience: string | null;
  topic_cluster: string | null;
  /**
   * True when the candidate is renderable (present in blog_posts_renderable)
   * AND exclude_from_recommendations = false.
   */
  eligible: boolean;
}

export type FeatureStripStatus =
  | "eligible"
  | "recommendation_excluded"
  | "not_renderable_or_missing";

export interface FeatureStripRow {
  id: string;
  slug: string;
  title: string;
  status: FeatureStripStatus;
}

export interface RecommendationInput {
  currentId: string;
  currentCluster: TopicCluster | string | null;
  candidates: RecommendationCandidate[];
}

export function pickRecommendations(input: RecommendationInput): RecommendationCandidate[] {
  const { currentId, currentCluster, candidates } = input;

  // Deduplicate by id and slug at the eligibility step so the invariant
  // matches the true number of distinct addressable candidates.
  const rawEligible = candidates.filter((c) => c.eligible && c.id !== currentId);
  const dedupSeenId = new Set<string>();
  const dedupSeenSlug = new Set<string>();
  const eligible: RecommendationCandidate[] = [];
  for (const c of rawEligible) {
    if (dedupSeenId.has(c.id) || dedupSeenSlug.has(c.slug)) continue;
    dedupSeenId.add(c.id);
    dedupSeenSlug.add(c.slug);
    eligible.push(c);
  }

  const seenId = new Set<string>();
  const seenSlug = new Set<string>();
  const out: RecommendationCandidate[] = [];
  const push = (c: RecommendationCandidate): void => {
    if (out.length >= 3) return;
    if (seenId.has(c.id) || seenSlug.has(c.slug)) return;
    seenId.add(c.id);
    seenSlug.add(c.slug);
    out.push(c);
  };

  // Stage 1 — same cluster
  if (currentCluster && isTopicCluster(currentCluster)) {
    for (const c of eligible) {
      if (c.topic_cluster === currentCluster) push(c);
    }
  }

  // Stages 2 + 3 — configured neighbours
  if (currentCluster && isTopicCluster(currentCluster)) {
    const map = NEIGHBOURS[currentCluster];
    for (const c of eligible) {
      if (map.same_audience.includes(c.topic_cluster as TopicCluster)) push(c);
    }
    for (const c of eligible) {
      if (map.cross_audience.includes(c.topic_cluster as TopicCluster)) push(c);
    }
  }

  // Stage 4 — separate site-wide fallback (only if still short)
  if (out.length < 3) {
    for (const c of eligible) push(c);
  }

  const target = Math.min(3, eligible.length);
  if (out.length !== target) {
    throw new Error(
      `recommendation cardinality invariant violated: got ${out.length}, expected ${target}`,
    );
  }
  return out;
}

/**
 * Classify a row for the feature strip surface. The classification is
 * independent of cluster sequencing — it only describes whether a row
 * should be shown.
 */
export function classifyFeatureStripRow(input: {
  id: string;
  slug: string;
  title: string;
  /** Present iff the row exists in blog_posts_renderable. */
  renderable: boolean;
  exclude_from_recommendations: boolean | null;
}): FeatureStripRow {
  if (!input.renderable) {
    return { id: input.id, slug: input.slug, title: input.title, status: "not_renderable_or_missing" };
  }
  if (input.exclude_from_recommendations === true) {
    return { id: input.id, slug: input.slug, title: input.title, status: "recommendation_excluded" };
  }
  return { id: input.id, slug: input.slug, title: input.title, status: "eligible" };
}